import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Avatar,
  Stack,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  Groups,
  Email,
  LinkedIn,
  GitHub,
  Twitter,
  Verified,
  LocationOn,
} from '@mui/icons-material';
import AboutSvg from '../assets/team.svg'; 

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Team members data
  const teamMembers = [
    {
      name: 'Shashank Girepunje',
      role: 'Co-Founder & CEO',
      image: 'https://i.pravatar.cc/300?img=12',
      bio: 'Visionary leader with 10+ years in tech',
      social: {
        linkedin: 'https://linkedin.com',
        github: 'https://github.com',
        twitter: 'https://twitter.com',
      },
    },
    {
      name: 'Rajendra Kumar Pandey',
      role: 'Co-Founder & CTO',
      image: 'https://i.pravatar.cc/300?img=5',
      bio: 'Full-stack expert passionate about innovation',
      social: {
        linkedin: 'https://linkedin.com',
        github: 'https://github.com',
      },
    },
    {
      name: 'Aman Kumar sah',
      role: 'Developer',
      image: 'https://i.pravatar.cc/300?img=33',
      bio: 'Product strategist building user-first experiences',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
      },
    },
    {
      name: 'Lilesh Lahre',
      role: 'Lead Designer',
      image: 'https://i.pravatar.cc/300?img=9',
      bio: 'Creative designer crafting beautiful interfaces',
      social: {
        linkedin: 'https://linkedin.com',
        github: 'https://github.com',
      },
    },
    {
      name: 'Anuj Malakar',
      role: 'Marketing Director',
      image: 'https://i.pravatar.cc/300?img=15',
      bio: 'Growth hacker scaling businesses nationwide',
      social: {
        linkedin: 'https://linkedin.com',
        twitter: 'https://twitter.com',
      },
    },
  ];

  return (
    <Box sx={{ bgcolor: '#F9FAFB', minHeight: '100vh', py: isMobile ? 4 : 8 }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: isMobile ? 6 : 10 }}>
          <Chip
            label="About Us"
            sx={{
              bgcolor: '#E8F5E9',
              color: '#1a7f3a',
              fontWeight: 700,
              fontSize: isMobile ? '0.75rem' : '0.85rem',
              mb: 2,
              px: 2,
              py: 2.5,
            }}
          />
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            fontWeight={800}
            gutterBottom
            sx={{ 
              color: '#1F2937',
              mb: 2,
              background: 'linear-gradient(135deg, #1a7f3a 0%, #0f5328 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Connecting Local Businesses<br />with Smart Shoppers
          </Typography>
          <Typography
            variant={isMobile ? 'body1' : 'h6'}
            color="text.secondary"
            sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.8, px: isMobile ? 2 : 0 }}
          >
            We're on a mission to help local businesses thrive by making it easier 
            for customers to discover amazing deals nearby. Join us in supporting 
            your community while saving money!
          </Typography>
        </Box>

        {/* Story Section - SVG LEFT, CONTENT RIGHT (Desktop) | SVG TOP, CONTENT BOTTOM (Mobile) */}
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 3 : 6,
            mb: isMobile ? 6 : 10,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Grid container spacing={isMobile ? 3 : 6} alignItems="center">
            {/* SVG Box - Shows FIRST on mobile, LEFT on desktop */}
            <Grid item xs={12} md={6} order={{ xs: 1, md: 1 }}>
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #1a7f3a 0%, #0f5328 100%)',
                  borderRadius: 3,
                  p: isMobile ? 3 : 4,
                  height: isMobile ? '250px' : '400px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* 🎨 REPLACE THIS WITH YOUR SVG */}
                <Box
                  component="img"
                  src={AboutSvg}
                  alt="Built by Shoppers"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',   
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))',
                  }}
                />
                {/* FALLBACK: If no SVG, shows this */}
                <TrendingUp 
                  sx={{ 
                    fontSize: isMobile ? 120 : 180, 
                    color: 'rgba(255,255,255,0.3)',
                    position: 'absolute',
                  }} 
                />
              </Box>
            </Grid>

            {/* Content Box - Shows SECOND on mobile, RIGHT on desktop */}
            <Grid item xs={12} md={6} order={{ xs: 2, md: 2 }}>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                fontWeight={700}
                gutterBottom
                sx={{ color: '#1F2937' }}
              >
                Built by Shoppers,<br />for Shoppers
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ lineHeight: 1.8, fontSize: isMobile ? '0.9rem' : '1rem' }}
              >
                Founded in 2024, Seek Deals started with a simple idea: make it easy 
                for people to find great deals from local businesses. We noticed that 
                amazing offers were often hidden or hard to discover.
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ lineHeight: 1.8, fontSize: isMobile ? '0.9rem' : '1rem' }}
              >
                Today, we're proud to connect thousands of customers with hundreds of 
                local vendors across India. Our platform helps businesses grow while 
                helping shoppers save money and discover new places.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 3 }}>
                <Chip
                  icon={<Verified />}
                  label="Verified Vendors"
                  sx={{
                    bgcolor: '#E8F5E9',
                    color: '#1a7f3a',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    mb: 1,
                  }}
                />
                <Chip
                  icon={<LocationOn />}
                  label="Location-Based"
                  sx={{
                    bgcolor: '#E8F5E9',
                    color: '#1a7f3a',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    mb: 1,
                  }}
                />
                <Chip
                  icon={<TrendingUp />}
                  label="Real-Time Deals"
                  sx={{
                    bgcolor: '#E8F5E9',
                    color: '#1a7f3a',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    mb: 1,
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>
        {/* CTA Section */}
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 4 : 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #1a7f3a 0%, #0f5328 100%)',
            borderRadius: 3,
            color: 'white',
          }}
        >
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} gutterBottom>
            Want to Join Our Journey?
          </Typography>
          <Typography
            variant={isMobile ? 'body2' : 'body1'}
            sx={{ mb: 3, opacity: 0.95, maxWidth: '600px', mx: 'auto' }}
          >
            We're always looking for talented people who share our passion for 
            supporting local businesses and creating amazing experiences.
          </Typography>
          <Box
            component="a"
            href="mailto:careers@seekdeals.com"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'white',
              color: '#1a7f3a',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 700,
              textDecoration: 'none',
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
              },
            }}
          >
            <Email /> careers@seekdeals.com
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default About;
